import React, { Component } from 'react';
import VideoDetails from 'model/VideoDetails';
import VideoTranscript from './VideoTranscript';
import styles from 'containers/Layouts/Layout.module.css';
import VideoPlayer from './VideoPlayer';
import DocumentNavigation from './DocumentNavigation';
import ContentService from 'services/ContentService';
import Back2Top from './Back2Top';
import i18n from 'i18n/I18nInstance';
import { FormattedMessage } from 'react-intl';
import { trackPromise } from 'react-promise-tracker';
import AnalyticsService from 'services/AnalyticsService';
import { ContentType, EventType } from '../model/enums';
import _ from 'classnames';

interface IProps {
    mediaKey: string;
    disablePrintMobileOnly: boolean;
}

interface IState {
    videoDetails: VideoDetails | null;
    error: string | null;
    isShowingTranscript: boolean;
}

export default class DisplayVideo extends Component<IProps, IState> {
    state: IState = {
        videoDetails: null,
        error: null,
        isShowingTranscript: false,
    };

    onDownloadButtonClicked = async (event: React.MouseEvent<HTMLImageElement, MouseEvent>) => {
        if (this.state.videoDetails) {
            let videoDetails = this.state.videoDetails;
            await trackPromise(
                ContentService.downloadPatientArticlePdf(
                    videoDetails.mediaKey!,
                    videoDetails.title!,
                ),
            );
            AnalyticsService.recordEventType(
                this.state.videoDetails,
                EventType.Download,
                ContentType.Video,
            );
        }
        event.stopPropagation();
    };

    onPrintButtonClicked = (event: React.MouseEvent<HTMLImageElement, MouseEvent>) => {
        if (this.state.isShowingTranscript) {
            window.print();
        } else {
            this.setState({ isShowingTranscript: true }, () => {
                window.print();
            });
        }
        event.stopPropagation();
        AnalyticsService.recordEventType(
            this.state.videoDetails,
            EventType.Print,
            ContentType.Video,
        );
    };

    async componentDidMount() {
        const mediaKey = decodeURIComponent(this.props.mediaKey);
        try {
            let videoDetails = (await trackPromise(
                ContentService.getPatientEducationVideo(mediaKey),
            )) as VideoDetails;

            videoDetails.mediaKey = mediaKey;
            this.setState({
                videoDetails: videoDetails,
            });
        } catch (error) {
            this.setState({ error: error.message });
        }
    }

    onTranscriptChange = () => {
        let newIsShowingTranscript = !this.state.isShowingTranscript;
        if (newIsShowingTranscript) {
            AnalyticsService.recordEventType(
                this.state.videoDetails,
                EventType.Click,
                ContentType.Transcript,
            );
        }
        this.setState({ isShowingTranscript: newIsShowingTranscript });
    };

    render() {
        const i18n_resources = i18n.translator.formatMessage({
            id: 'common.Resources',
            defaultMessage: 'RESOURCES',
        });
        if (this.state.error) {
            return (
                <div>
                    <DocumentNavigation
                        goBackText={i18n_resources}
                        showActionButtons={false}
                    ></DocumentNavigation>
                    <h1>
                        <FormattedMessage
                            id="displayVideo.ProblemsVideo"
                            defaultMessage="Problems getting video details"
                        />
                        : {this.state.error}
                    </h1>
                </div>
            );
        }
        // this skips the first render when nothing is ready and it displays an error
        if (this.state.videoDetails === null) {
            return <div />;
        }
        let title;
        if (this.state.videoDetails.title) {
            title = (
                <div data-qaid="videoTitle" className={styles.educationTitle}>
                    {this.state.videoDetails.title}
                </div>
            );
        }
        let blurb;
        if (this.state.videoDetails.blurb) {
            blurb = (
                <div data-qaid="videoBlurb" className={styles.educationText}>
                    {this.state.videoDetails.blurb}
                </div>
            );
        }
        let endStatement;
        if (this.state.videoDetails.endStatement) {
            const endStatementHtml = { __html: this.state.videoDetails.endStatement };
            endStatement = (
                <div
                    data-qaid="videoEndStatement"
                    className={styles.endStatement}
                    dangerouslySetInnerHTML={endStatementHtml}
                />
            );
        }
        let bottomNavigation;
        if (this.state.isShowingTranscript) {
            bottomNavigation = (
                <div
                    data-qaid="videoBottomNavigation"
                    className={_(styles.videoBottomNavigation, styles.noPrint)}
                >
                    <DocumentNavigation
                        goBackText={i18n_resources}
                        showActionButtons={false}
                    ></DocumentNavigation>
                </div>
            );
        }

        const isMobile = () => {
            const UA = navigator.userAgent;
            // Test for Mobi in the user agent. Recommended by mozilla, here:
            // https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent#mobile_tablet_or_desktop
            return /Mobi/g.test(UA);
        };

        const disablePrint = isMobile() && this.props.disablePrintMobileOnly;

        return (
            <div className={styles.educationRoot} data-qaid="videoRoot">
                <div className={styles.videoDisplay} data-qaid="videoDisplay">
                    &nbsp;
                    <DocumentNavigation
                        goBackText={i18n_resources}
                        actionsButtonsDownloadHandler={this.onDownloadButtonClicked}
                        actionsButtonsPrintHandler={this.onPrintButtonClicked}
                        showActionButtons={!disablePrint}
                        documentTitle={this.state.videoDetails.title}
                    ></DocumentNavigation>
                    {title}
                    {blurb}
                    <div className={styles.noPrint}>
                        <VideoPlayer data-qaid="videoPlayer" {...this.state.videoDetails} />
                    </div>
                    &nbsp;
                    <VideoTranscript
                        transcript={this.state.videoDetails.transcript}
                        isShowingTranscript={this.state.isShowingTranscript}
                        onTranscriptVisibilityChange={this.onTranscriptChange}
                    />
                    <Back2Top />
                    &nbsp;
                    {endStatement}
                    &nbsp;
                    {bottomNavigation}
                </div>
            </div>
        );
    }
}
